import React, { useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconLogout,
  IconMail,
  IconShield,
  IconUser,
  IconUserCircle,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { usePerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { NonPaginatedResponse } from "../../../lib/types";
import { IAdminProps } from "../../../lib/types/Admin";
import { logout } from "../../../services/auth";
import { HttpCodes } from "../../../lib/data";

export default function Profile() {
  const navigate = useNavigate();
  const [logoutModalOpened, setLogoutModalOpened] = useState(false);

  const { data, isLoading, status } = usePerformRequest<
    NonPaginatedResponse<IAdminProps>
  >({
    method: Endpoints.GetProfile().method,
    route: Endpoints.GetProfile().route,
  });

  const profile = useMemo(() => data?.data, [data]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isUnauthorized = status === HttpCodes.Unauthorized;

  return (
    <div className="bg-gradient-to-br from-teal-50 via-white to-cyan-50 min-h-[70vh] p-2 md:p-4 lg:p-6">
      <Stack gap="lg">
        <Card
          padding="xl"
          radius="lg"
          shadow="lg"
          className="bg-white border border-gray-100"
        >
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Badge
                size="lg"
                variant="light"
                leftSection={<IconShield size={16} />}
                color="teal"
              >
                Admin Profile
              </Badge>
              <Title order={2} className="text-3xl font-bold text-gray-900">
                Welcome back{profile?.name ? `, ${profile.name}` : ""} 👋
              </Title>
              <Text c="dimmed" size="sm" className="max-w-2xl">
                Review your account details and sign out securely.
              </Text>
            </Stack>
            <Button
              variant="gradient"
              gradient={{ from: "teal", to: "cyan" }}
              leftSection={<IconUserCircle size={18} />}
              onClick={() => setLogoutModalOpened(true)}
            >
              View Profile / Logout
            </Button>
          </Group>
        </Card>

        <Paper
          radius="lg"
          shadow="md"
          p="xl"
          className="bg-white border border-gray-100"
        >
          <Group align="flex-start" gap="xl" wrap="wrap">
            <Avatar
              size={96}
              radius="xl"
              color="teal"
              variant="gradient"
              gradient={{ from: "teal", to: "cyan" }}
            >
              {profile?.name ? profile.name[0]?.toUpperCase() : <IconUser />}
            </Avatar>
            <Stack gap="sm">
              <Title order={3} className="text-2xl text-gray-900">
                {isLoading ? <Skeleton width={160} height={28} /> : profile?.name ?? "—"}
              </Title>
              <Group gap="xs">
                <IconMail size={16} className="text-teal-600" />
                {isLoading ? (
                  <Skeleton width={200} height={18} />
                ) : (
                  <Text size="sm" c="dimmed">
                    {profile?.email ?? "No email"}
                  </Text>
                )}
              </Group>
              <Group gap="xs">
                <IconShield size={16} className="text-teal-600" />
                <Text size="sm" c="dimmed">
                  Role: Administrator
                </Text>
              </Group>
              <Group gap="sm" mt="sm">
                <Badge color="teal" variant="light">
                  Secure Session
                </Badge>
                <Badge color="cyan" variant="light">
                  Dashboard Access
                </Badge>
              </Group>
            </Stack>
          </Group>

          <Divider my="lg" />

          <Group gap="md">
            <Button
              variant="gradient"
              gradient={{ from: "teal", to: "cyan" }}
              leftSection={<IconUserCircle size={18} />}
              onClick={() => setLogoutModalOpened(true)}
            >
              Open Profile Modal
            </Button>
            <Button
              variant="outline"
              color="red"
              leftSection={<IconLogout size={18} />}
              onClick={() => setLogoutModalOpened(true)}
            >
              Logout
            </Button>
          </Group>
        </Paper>
      </Stack>

      <Modal
        opened={logoutModalOpened}
        onClose={() => setLogoutModalOpened(false)}
        title="Your Profile"
        centered
        size="lg"
      >
        {isUnauthorized ? (
          <Stack gap="sm">
            <Text c="red" fw={600}>
              Session expired
            </Text>
            <Text c="dimmed" size="sm">
              Your session is no longer valid. Please log in again.
            </Text>
            <Button
              color="red"
              leftSection={<IconLogout size={18} />}
              onClick={handleLogout}
            >
              Go to login
            </Button>
          </Stack>
        ) : (
          <Stack gap="md">
            <Group gap="md">
              <Avatar
                size={72}
                radius="xl"
                color="teal"
                variant="gradient"
                gradient={{ from: "teal", to: "cyan" }}
              >
                {profile?.name ? profile.name[0]?.toUpperCase() : <IconUser />}
              </Avatar>
              <Stack gap={4}>
                <Title order={4}>{profile?.name ?? "Loading..."}</Title>
                <Group gap="xs">
                  <IconMail size={16} className="text-teal-600" />
                  <Text size="sm" c="dimmed">
                    {profile?.email ?? "Loading..."}
                  </Text>
                </Group>
                <Badge color="teal" variant="light" leftSection={<IconShield size={14} />}>
                  Admin
                </Badge>
              </Stack>
            </Group>

            <Divider />

            <Text size="sm" c="dimmed">
              Manage your session securely. When you logout, you will be redirected to the
              admin login screen.
            </Text>

            <Button
              color="red"
              fullWidth
              size="md"
              leftSection={<IconLogout size={18} />}
              onClick={handleLogout}
            >
              Confirm Logout
            </Button>
          </Stack>
        )}
      </Modal>
    </div>
  );
}

